/**
 * AUTO-GENERATED — do not edit by hand.
 *
 * Source     : scripts/taxonomy/taxonomy.json
 * Regenerate : cd app/frontend && npm run gen:taxonomy
 *
 * Runtime vocabulary for the Global Help taxonomy resolver (KAN-1358). Each term
 * is a canonical UI concept plus the synonyms users say for it. The resolver
 * scores an inbound message against `ui` + `aliases` to produce ranked
 * taxonomy_matches[] that ground Global Help routing and guidance.
 */

export type TaxonomyConfidence = 'seed' | 'auto';

export interface TaxonomyTerm {
  /** Stable kebab-case slug id. */
  id: string;
  /** Canonical UI label. */
  ui: string;
  /** Synonyms/aliases users may say for this concept. */
  aliases: string[];
  /** Curation confidence: 'seed' (hand-curated) or 'auto' (extracted). */
  confidence: TaxonomyConfidence;
  /** True when cross-layer evidence for the term is still incomplete. */
  unconfirmed: boolean;
}

/** Taxonomy schema/content version — surfaced to the router as taxonomy_version. */
export const TAXONOMY_VERSION = "1.0";

/** Environment the curated taxonomy was extracted from. */
export const TAXONOMY_SOURCE_ENV = "sandbox";

/** All taxonomy terms, sorted by id for stable diffs. */
export const TAXONOMY_TERMS: readonly TaxonomyTerm[] = [
  {
    id: "activity-event",
    ui: "Activity Event",
    aliases: ["Activity Log"],
    confidence: "seed",
    unconfirmed: true,
  },
  {
    id: "brainstorm",
    ui: "Brainstorm",
    aliases: ["Brainstorms"],
    confidence: "seed",
    unconfirmed: false,
  },
  {
    id: "chat",
    ui: "Chat",
    aliases: ["Workproduct Chat History", "Conversation", "Chat History"],
    confidence: "seed",
    unconfirmed: true,
  },
  {
    id: "company",
    ui: "Company",
    aliases: ["Company"],
    confidence: "seed",
    unconfirmed: true,
  },
  {
    id: "context-mesh",
    ui: "Context Mesh",
    aliases: ["Product Meshes", "Product Mesh", "product-mesh"],
    confidence: "seed",
    unconfirmed: false,
  },
  {
    id: "epic",
    ui: "Epic",
    aliases: ["Epics"],
    confidence: "seed",
    unconfirmed: true,
  },
  {
    id: "feature",
    ui: "Feature",
    aliases: ["Feature:", "Features"],
    confidence: "seed",
    unconfirmed: true,
  },
  {
    id: "feature-studio",
    ui: "Feature Studio",
    aliases: [],
    confidence: "seed",
    unconfirmed: true,
  },
  {
    id: "file-integration-hub",
    ui: "File Integration Hub",
    aliases: ["Integrations", "Integration Hub", "File Hub"],
    confidence: "seed",
    unconfirmed: true,
  },
  {
    id: "milestone",
    ui: "Milestone",
    aliases: [],
    confidence: "auto",
    unconfirmed: true,
  },
  {
    id: "persona",
    ui: "Persona",
    aliases: ["Personas", "User Persona"],
    confidence: "seed",
    unconfirmed: false,
  },
  {
    id: "product-hub",
    ui: "Product Hub",
    aliases: ["Hub"],
    confidence: "seed",
    unconfirmed: true,
  },
  {
    id: "product-studio-console",
    ui: "Product Studio Console",
    aliases: ["Product Studio", "Studio Console"],
    confidence: "seed",
    unconfirmed: true,
  },
  {
    id: "project",
    ui: "Project",
    aliases: ["Project", "Projects"],
    confidence: "seed",
    unconfirmed: true,
  },
  {
    id: "skill",
    ui: "Skill",
    aliases: ["Skills", "Skill Runs"],
    confidence: "seed",
    unconfirmed: false,
  },
  {
    id: "subscription",
    ui: "Subscription",
    aliases: ["Subscription Plan", "Billing"],
    confidence: "seed",
    unconfirmed: true,
  },
  {
    id: "user",
    ui: "User",
    aliases: ["Users"],
    confidence: "seed",
    unconfirmed: true,
  },
  {
    id: "user-state",
    ui: "User State",
    aliases: ["User Client State"],
    confidence: "seed",
    unconfirmed: false,
  },
  {
    id: "user-stories",
    ui: "User Stories",
    aliases: [],
    confidence: "auto",
    unconfirmed: true,
  },
  {
    id: "work-product",
    ui: "Work Product",
    aliases: ["WorkProduct", "Work Products", "Deliverable"],
    confidence: "seed",
    unconfirmed: true,
  },
  {
    id: "work-product-editor",
    ui: "Work Product Editor",
    aliases: ["Editor", "Work Editor"],
    confidence: "seed",
    unconfirmed: true,
  },
];

/** Terms keyed by id for O(1) lookup. */
export const TAXONOMY_BY_ID: Readonly<Record<string, TaxonomyTerm>> = Object.freeze(
  Object.fromEntries(TAXONOMY_TERMS.map((t) => [t.id, t])),
);
